import { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useTranslation } from "react-i18next";

const color = (m) => (m >= 6 ? "#DC2626" : m >= 4.5 ? "#EA580C" : "#CA8A04");
const RECENT_MS = 3 * 3600 * 1000; // resaltar (pulso) réplicas de las últimas 3 h

// Mapa interactivo de réplicas con Leaflet + Carto Voyager (sin API key, apto 3G).
export function LeafletQuakesMap({ quakes, timeAgo }) {
  const { t, i18n } = useTranslation();
  const ref = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);
  const legendRef = useRef(null);
  const legendHtml = () =>
    `<i style="background:#DC2626"></i>${t("map.legendStrong")}<br><i style="background:#EA580C"></i>${t("map.legendModerate")}<br><i style="background:#CA8A04"></i>${t("map.legendLight")}`;

  useEffect(() => {
    if (!ref.current || mapRef.current) return;
    // dragging:false → un dedo desplaza la página, no el mapa (evita el "scroll trap").
    // El zoom sigue disponible con pellizco (2 dedos), doble toque y botones +/-.
    const map = L.map(ref.current, {
      zoomControl: true,
      scrollWheelZoom: false,
      dragging: false,
      touchZoom: true,
      doubleClickZoom: true,
    }).setView([10.5, -67.6], 7);
    L.tileLayer("https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png", {
      attribution: "© OpenStreetMap, © CARTO", maxZoom: 18,
    }).addTo(map);
    const legend = L.control({ position: "bottomleft" });
    legend.onAdd = () => {
      const d = L.DomUtil.create("div", "quake-legend");
      d.innerHTML = legendHtml();
      legendRef.current = d;
      return d;
    };
    legend.addTo(map);
    layerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    const sizeTimer = setTimeout(() => map.invalidateSize(), 200);
    return () => { clearTimeout(sizeTimer); map.remove(); mapRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refresca la leyenda cuando cambia el idioma.
  useEffect(() => {
    if (legendRef.current) legendRef.current.innerHTML = legendHtml();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  useEffect(() => {
    const map = mapRef.current, layer = layerRef.current;
    if (!map || !layer) return;
    layer.clearLayers();
    const bounds = [];
    quakes.forEach((q) => {
      if (q.lat == null || q.lng == null) return;
      const c = color(q.mag);
      const size = Math.round(16 + (q.mag || 2.5) * 5);
      const recent = Date.now() - q.time < RECENT_MS;
      const icon = L.divIcon({
        className: "quake-marker",
        html: `<span class="quake-dot${recent ? " quake-pulse" : ""}" style="width:${size}px;height:${size}px;background:${c}66;border-color:${c}"><b>${q.mag?.toFixed(1)}</b></span>`,
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2],
      });
      const m = L.marker([q.lat, q.lng], { icon }).addTo(layer);
      const depth = q.depth != null ? `<span class="qd-row">${t("map.depth", { km: Math.round(q.depth) })}</span>` : "";
      const recentTag = recent ? `<span class="qd-recent">${t("map.recent")}</span>` : "";
      m.bindPopup(
        `<div class="quake-pop">
          <b style="color:${c}">${t("map.magnitude", { m: q.mag?.toFixed(1) })}</b>${recentTag}
          <span class="qd-place">${q.place}</span>
          <span class="qd-row">${timeAgo(q.time)} · ${t("map.distance", { km: q.dist })}</span>
          ${depth}
          <span class="qd-src">${t("map.source", { src: q.src || "USGS" })}</span>
        </div>`,
      );
      bounds.push([q.lat, q.lng]);
    });
    if (bounds.length) {
      map.fitBounds(bounds, { padding: [50, 50] });
      if (map.getZoom() > 9) map.setZoom(9);
    }
  }, [quakes, timeAgo, t]);

  return <div ref={ref} data-testid="quakes-map" className="w-full h-72 bg-neutral-100" />;
}
